import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
    ApplicationRoleCode,
    CorporationPermissionCreateRequest,
    CorporationUserRole,
    ICorporationResponse,
    ILocationResponse,
    IUserCorporationChild,
    IUserLocationChild,
    IUserResponse,
    LocationPermissionCreateRequest,
    LocationUserRole,
} from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { Detail } from 'components/activity/Detail';
import { IconButton } from 'components/button/IconButton';
import { DocumentTitle } from 'components/DocumentTitle';
import { DateTimeDisplay } from 'components/date_time/DateTimeDisplay';
import { CollapsiblePane } from 'components/expandable_pane/CollapsiblePane';
import { UserNameInlineEditor } from 'components/inline_edit/UserNameInlineEditor';
import { ConfirmModal } from 'components/modal/ConfirmModal';
import { NoValue } from 'components/no_value/NoValue';
import { DetailsPage } from 'components/page/DetailsPage';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import {
    specificUserQueryKey,
    useSpecificUserInfo,
} from 'custom_hooks/data_hooks/user/useSpecificUserInfo';
import { useUserDateTimeFormat } from 'custom_hooks/useUserDateTimeFormat';
import { SettingsHeading } from 'pages/Settings/shared/styled';
import { AddCorporationToUserModal } from 'pages/Settings/Users/AddCorporationToUserModal';
import { AddLocationToUserModal } from 'pages/Settings/Users/AddLocationToUserModal';
import { TriggerPasswordResetButton } from 'pages/Settings/Users/button/TriggerResetPasswordButton';
import { CorporationsForUserTable } from 'pages/Settings/Users/CorporationsForUserTable';
import { LocationsForUserTable } from 'pages/Settings/Users/LocationsForUserTable';
import { UserApplicationRoleCodeCell } from 'pages/Settings/Users/table_cells/UserApplicationRoleCodeCell';
import { UserApprovedCell } from 'pages/Settings/Users/table_cells/UserApprovedCell';
import { UserEmailCell } from 'pages/Settings/Users/table_cells/UserEmailCell';
import { UserLockedOutCell } from 'pages/Settings/Users/table_cells/UserLockedOutCell';
import { UserTagsCell } from 'pages/Settings/Users/table_cells/UserTagsCell';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { isValidationProblemDetails } from 'utils/api';
import { queryClient } from 'utils/query';

type SingleUsersViewParams = {
    userId?: string;
};

export type SingleUsersViewProps = {
    user?: IUserResponse;
    corporation?: ICorporationResponse;
};

export function SingleUserPage({ user, corporation }: SingleUsersViewProps) {
    const { showToast } = useNotificationContext();
    const { canAdminEditUserProperties, canManageUserTags } = usePermissions();

    const { userId: paramsUserId } = useParams<SingleUsersViewParams>();

    const {
        data: selectedUserInfo,
        refetch: refetchUserInfo,
        isLoading: isLoadingUserInfo,
        isFetching,
        error,
    } = useSpecificUserInfo(user?.Id ?? paramsUserId ?? '');

    const { formatDate } = useUserDateTimeFormat();

    const showCorporationAndLocationAssignments =
        selectedUserInfo?.ApplicationRoleCode === ApplicationRoleCode.User ||
        selectedUserInfo?.ApplicationRoleCode === ApplicationRoleCode.Sales;

    const { mutate: triggerPasswordReset, isPending: processing } = useMutation({
        meta: { name: 'trigger-password-reset' },
        mutationFn: async () => {
            if (selectedUserInfo) {
                await sdClient.adminResetPasswordSD4({ body: selectedUserInfo.Id });
                refetchUserInfo();
            }
        },

        onSuccess: () =>
            showToast({
                type: 'success',
                content: `Password reset email successfully sent to ${selectedUserInfo?.Email}.`,
            }),

        onError: () =>
            showToast({
                type: 'error',
                content:
                    'Failed to process password reset request. Please contact KCF Support.',
            }),
    });

    return (
        <DetailsPage
            isLoading={isLoadingUserInfo || isFetching}
            invalidURL={!selectedUserInfo && !isLoadingUserInfo && !!error}
            fullHeight
            fullWidth
            noPageScroll
        >
            <DocumentTitle
                title={`${selectedUserInfo?.FirstName ?? ''} ${
                    selectedUserInfo?.LastName ?? ''
                } User Settings`}
            />
            {selectedUserInfo && (
                <div className="p-35" style={{ overflowY: 'auto' }}>
                    <SettingsHeading>
                        <UserNameInlineEditor
                            userId={selectedUserInfo.Id}
                            firstName={selectedUserInfo.FirstName}
                            lastName={selectedUserInfo.LastName}
                            url={null}
                        />
                    </SettingsHeading>
                    <div className="mt-20">
                        <Detail label="Application Role" labelClassName="text-nowrap">
                            <UserApplicationRoleCodeCell
                                rowData={selectedUserInfo}
                                onChange={refetchUserInfo}
                            />
                        </Detail>
                        <Detail label="Email" labelClassName="text-nowrap">
                            <UserEmailCell
                                rowData={selectedUserInfo}
                                onChange={refetchUserInfo}
                            />
                        </Detail>
                        <Detail label="Phone Number" labelClassName="text-nowrap">
                            {selectedUserInfo.PhoneNumber ?? <NoValue />}
                        </Detail>
                        <Detail label="Last Login" labelClassName="text-nowrap">
                            {selectedUserInfo.LastLogin
                                ? formatDate(selectedUserInfo.LastLogin)
                                : 'Never'}
                        </Detail>
                        <Detail label="Last Active" labelClassName="text-nowrap">
                            <DateTimeDisplay
                                date={selectedUserInfo.LastRequestTime}
                                fallback={<span>Never</span>}
                            />
                        </Detail>
                        <Detail label="Accepted EULA" labelClassName="text-nowrap">
                            {selectedUserInfo.AcceptedEula ? 'Yes' : 'No'}
                        </Detail>
                        <Detail label="Locked Out" labelClassName="text-nowrap">
                            <UserLockedOutCell
                                rowData={selectedUserInfo}
                                onChange={refetchUserInfo}
                            />
                        </Detail>
                        <Detail label="Is Approved" labelClassName="text-nowrap">
                            <UserApprovedCell
                                rowData={selectedUserInfo}
                                onChange={refetchUserInfo}
                            />
                        </Detail>
                        {canAdminEditUserProperties && (
                            <>
                                <Detail label="Identity Provider">
                                    {selectedUserInfo.IdentityProviderName ??
                                        'SMARTdiagnostics'}
                                </Detail>
                                <Detail label="Last Password Change">
                                    {selectedUserInfo.LastPasswordChange
                                        ? formatDate(selectedUserInfo.LastPasswordChange)
                                        : 'Never'}
                                </Detail>
                                <Detail label="Password Reset Pending">
                                    {selectedUserInfo.IsPasswordResetPending
                                        ? 'Yes'
                                        : 'No'}
                                </Detail>
                                <TriggerPasswordResetButton
                                    processing={processing}
                                    disabled={
                                        selectedUserInfo.IdentityProviderName !==
                                            undefined ||
                                        processing ||
                                        !selectedUserInfo.IsApproved
                                    }
                                    onResetClick={triggerPasswordReset}
                                />
                            </>
                        )}
                        {canManageUserTags && (
                            <Detail label="Tags" labelClassName="text-nowrap">
                                <UserTagsCell userId={selectedUserInfo.Id} />
                            </Detail>
                        )}
                    </div>
                    {showCorporationAndLocationAssignments && (
                        <UserOrganizationRoles
                            appContextCorporationId={corporation?.Id}
                            {...{
                                selectedUserInfo,
                                refetchUserInfo,
                            }}
                        />
                    )}
                </div>
            )}
        </DetailsPage>
    );
}

function UserOrganizationRoles({
    appContextCorporationId,
    selectedUserInfo,
    refetchUserInfo,
}: {
    /**
     * The id of the corporation in the current scope
     */
    appContextCorporationId: string | undefined;
    selectedUserInfo: IUserResponse;
    refetchUserInfo: () => void;
}) {
    const { canAdminEditUserProperties } = usePermissions();
    const { showToast } = useNotificationContext();
    const { location: browserLocation, push: pushBrowserHistory } = useHistory();

    const [locationsPaneExpanded, setLocationsPaneExpanded] = useState(false);
    const [corporationsPaneExpanded, setCorporationsPaneExpanded] = useState(false);

    const [addLocationModalOpen, setAddLocationModalOpen] = useState(false);
    const [addCorporationModalOpen, setAddCorporationModalOpen] = useState(false);

    const [locationAssignmentToDelete, setLocationAssignmentToDelete] =
        useState<IUserLocationChild>();
    const [corporationAssignmentToDelete, setCorporationAssignmentToDelete] =
        useState<IUserCorporationChild>();

    const onDelete = (removedPermission: IUserLocationChild | IUserCorporationChild) => {
        if (
            !canAdminEditUserProperties &&
            isLastViewablePermission(selectedUserInfo, removedPermission)
        ) {
            pushBrowserHistory(
                browserLocation.pathname.substring(
                    0,
                    browserLocation.pathname.lastIndexOf('/'),
                ),
            );
        }
    };

    const onAddCorporationToUser = async (
        corporationToAdd: ICorporationResponse,
        isAdmin: boolean,
    ) => {
        const isAlreadyAdded = selectedUserInfo.Corporations.some(
            (corp) => corp.CorporationId === corporationToAdd.Id,
        );

        if (!isAlreadyAdded) {
            await sdClient.addCorporationPermission({
                userId: selectedUserInfo.Id,
                body: new CorporationPermissionCreateRequest({
                    CorporationId: corporationToAdd.Id,
                    CorporationUserRole: isAdmin
                        ? CorporationUserRole.Admin
                        : CorporationUserRole.User,
                }),
            });
        } else {
            showToast({ type: 'error', content: 'Corporation already exists.' });
        }

        queryClient.invalidateQueries({
            queryKey: [specificUserQueryKey],
        });
        refetchUserInfo();

        setAddCorporationModalOpen(false);
    };

    const onAddLocationToUser = async (
        locationToAdd: ILocationResponse,
        isAdmin: boolean,
    ) => {
        const isAlreadyAdded = selectedUserInfo.Locations.some(
            (loc) => loc.LocationId === locationToAdd.Id,
        );

        if (!isAlreadyAdded) {
            await sdClient.addLocationPermission({
                userId: selectedUserInfo.Id,
                body: new LocationPermissionCreateRequest({
                    LocationId: locationToAdd.Id,
                    LocationUserRole: isAdmin
                        ? LocationUserRole.Admin
                        : LocationUserRole.User,
                }),
            });
        } else {
            showToast({ type: 'error', content: 'Location already exists.' });
        }

        queryClient.invalidateQueries({
            queryKey: [specificUserQueryKey],
        });
        refetchUserInfo();

        setAddLocationModalOpen(false);
    };

    const removeCorporationAssignment = async () => {
        if (corporationAssignmentToDelete?.CorporationId) {
            try {
                await sdClient.deleteCorporationPermission({
                    userId: selectedUserInfo.Id,
                    corporationId: corporationAssignmentToDelete.CorporationId,
                });
                onDelete(corporationAssignmentToDelete);
                queryClient.invalidateQueries({
                    queryKey: [specificUserQueryKey],
                });
                refetchUserInfo();
                setCorporationAssignmentToDelete(undefined);
                showToast({
                    type: 'success',
                    content: 'Successfully removed corporation assignment from user.',
                });
            } catch (e) {
                showToast({
                    type: 'error',
                    content: deleteCorporationAssignmentErrorMessage(e),
                });
            }
        }
    };

    const removeLocationAssignment = async () => {
        if (locationAssignmentToDelete?.LocationId) {
            try {
                await sdClient.deleteLocationPermission({
                    userId: selectedUserInfo.Id,
                    locationId: locationAssignmentToDelete.LocationId,
                });
                onDelete(locationAssignmentToDelete);
                queryClient.invalidateQueries({
                    queryKey: [specificUserQueryKey],
                });
                refetchUserInfo();
                setLocationAssignmentToDelete(undefined);
                showToast({
                    type: 'success',
                    content: 'Successfully removed location assignment from user.',
                });
            } catch (e) {
                showToast({
                    type: 'error',
                    content: deleteLocationAssignmentErrorMessage(e),
                });
            }
        }
    };

    const onDeleteModalClose = () => {
        setCorporationAssignmentToDelete(undefined);
        setLocationAssignmentToDelete(undefined);
    };

    return (
        <>
            <CollapsiblePane
                title="Corporations"
                expanded={corporationsPaneExpanded}
                onChange={setCorporationsPaneExpanded}
                className="mb-10"
            >
                <div className="p-20">
                    <div className="flex-row justify-flex-end mb-15">
                        <IconButton
                            icon={faPlus}
                            onClick={() => setAddCorporationModalOpen(true)}
                        >
                            Add
                        </IconButton>
                    </div>
                    <CorporationsForUserTable
                        selectedUserInfo={selectedUserInfo}
                        onChange={refetchUserInfo}
                        onDeleteCorporationForUserClick={(userCorporationChild) =>
                            setCorporationAssignmentToDelete(userCorporationChild)
                        }
                    />
                </div>
            </CollapsiblePane>
            <CollapsiblePane
                title="Locations"
                expanded={locationsPaneExpanded}
                onChange={setLocationsPaneExpanded}
            >
                <div className="p-20">
                    <div className="flex-row justify-flex-end mb-15">
                        <IconButton
                            icon={faPlus}
                            onClick={() => setAddLocationModalOpen(true)}
                        >
                            Add
                        </IconButton>
                    </div>
                    <LocationsForUserTable
                        selectedUserInfo={selectedUserInfo}
                        onChange={refetchUserInfo}
                        onDeleteLocationForUserClick={(userLocationChild) =>
                            setLocationAssignmentToDelete(userLocationChild)
                        }
                    />
                </div>
            </CollapsiblePane>
            {addCorporationModalOpen && (
                <AddCorporationToUserModal
                    onClose={() => setAddCorporationModalOpen(false)}
                    onCreate={(corp: ICorporationResponse, isAdmin: boolean) =>
                        onAddCorporationToUser(corp, isAdmin)
                    }
                />
            )}
            {addLocationModalOpen && (
                <AddLocationToUserModal
                    onClose={() => setAddLocationModalOpen(false)}
                    onCreate={(location: ILocationResponse, isAdmin: boolean) =>
                        onAddLocationToUser(location, isAdmin)
                    }
                    corporationId={appContextCorporationId}
                />
            )}
            {corporationAssignmentToDelete && (
                <ConfirmModal
                    title={`Remove ${corporationAssignmentToDelete.CorporationName} assignment for ${selectedUserInfo.FirstName} ${selectedUserInfo.LastName}`}
                    actionText="remove this corporation assignment?"
                    checkText="remove corporation assignment from user"
                    actionButtonText="Remove"
                    onAction={removeCorporationAssignment}
                    onClose={onDeleteModalClose}
                />
            )}
            {locationAssignmentToDelete && (
                <ConfirmModal
                    title={`Remove ${locationAssignmentToDelete.LocationName} assignment for ${selectedUserInfo.FirstName} ${selectedUserInfo.LastName}`}
                    actionText="remove this location assignment?"
                    checkText="remove location assignment from user"
                    actionButtonText="Remove"
                    onAction={removeLocationAssignment}
                    onClose={onDeleteModalClose}
                />
            )}
        </>
    );
}

const isLocationChild = (
    assignment: IUserLocationChild | IUserCorporationChild,
): assignment is IUserLocationChild => 'LocationId' in assignment;

const isLastViewablePermission = (
    user: IUserResponse,
    permission: IUserLocationChild | IUserCorporationChild,
) =>
    !(isLocationChild(permission)
        ? // Only "viewable" permissions are returned from the api, so these checks
          // can omit the current user's permissions
          user.Locations.some((l) => l.LocationId !== permission.LocationId) ||
          // If the current user can see another corporation role, or if the
          // existing role is not CorporationNone (Will not be auto-removed)
          user.Corporations.some(
              (c) =>
                  c.CorporationId !== permission.LocationCorporationId ||
                  c.CorporationUserRole !== CorporationUserRole.None,
          )
        : user.Corporations.some((c) => c.CorporationId !== permission.CorporationId));

export const deleteCorporationAssignmentErrorMessage = (error: unknown) => {
    if (isValidationProblemDetails(error)) {
        const errors = [
            error.Detail,
            ...Object.values(error.Errors ?? {}).flatMap((e) => e),
        ];
        const hasError = (msg: string) => errors.some((err) => err?.includes(msg));
        if (hasError('Account must have an associated corporation permission')) {
            return 'One or more Location assignments depend on this Corporation assignment';
        }
        if (hasError('Users must have at least one corporation or location permission')) {
            return 'Users must have at least one corporation or location permission';
        }
    }
    return 'An unexpected error occurred while deleting corporation assignment';
};

export const deleteLocationAssignmentErrorMessage = (error: unknown) => {
    if (isValidationProblemDetails(error)) {
        const errors = [
            error.Detail,
            ...Object.values(error.Errors ?? {}).flatMap((e) => e),
        ];
        const hasError = (msg: string) => errors.some((err) => err?.includes(msg));
        if (hasError('Users must have at least one corporation or location permission')) {
            return 'Users must have at least one corporation or location permission';
        }
    }
    return 'An unexpected error occurred while deleting location assignment';
};
